import { useQuery } from '@tanstack/react-query';
import { useApi } from '../../app/providers';
import type { Api } from '../../lib/api';
import { mediaUrl } from '../../lib/media-url';

type Pending = { ids: Set<string>; waiters: ((m: Map<string, string>) => void)[]; fail: ((e: unknown) => void)[] };

const batches = new WeakMap<Api, Pending>();

/** Cards ask one by one; the ids asked within the same tick go out as one `board_thumbnails` call. */
function loadThumbnail(api: Api, boardId: string): Promise<string | null> {
  let pending = batches.get(api);
  if (!pending) {
    const p: Pending = { ids: new Set(), waiters: [], fail: [] };
    pending = p;
    batches.set(api, p);
    setTimeout(() => {
      batches.delete(api);
      api.boardThumbnails([...p.ids]).then(
        (m) => p.waiters.forEach((w) => w(m)),
        (e: unknown) => p.fail.forEach((f) => f(e)),
      );
    }, 0);
  }
  pending.ids.add(boardId);
  const p = pending;
  return new Promise((resolve, reject) => {
    p.waiters.push((m) => {
      const key = m.get(boardId);
      resolve(key ? mediaUrl(key) : null);
    });
    p.fail.push(reject);
  });
}

/** URL of a board's 640 px preview, null when it has none yet. */
export function useBoardThumbnailUrl(boardId: string): string | null {
  const api = useApi();
  const q = useQuery({ queryKey: ['thumbnail', boardId], queryFn: () => loadThumbnail(api, boardId), staleTime: 60_000 });
  return q.data ?? null;
}
